<script lang="ts">
  import { formatIST } from '$lib/utils/dates';
  // Admin > Email access: the leak-audit view — one screen answering "who
  // can read what". Grouped by vertical (a user appears under every
  // vertical they can access — primary + UserVertical grants); admins get
  // their own top section since they see every vertical's mail regardless
  // of grants. Reuses the same GET /admin/users (extended with
  // email_account_* fields, see handlers.emailAccountsByUserID) and PUT
  // /admin/users/:id mutation as the Users & Access tab's email_access
  // toggle — this is a second view onto the same data, not a new write path.
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Mail, ShieldCheck } from 'lucide-svelte';
  import { adminUsersApi, adminVerticalsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import type { AdminUser } from '$lib/types';

  const queryClient = useQueryClient();

  // ponytail: no pagination — this is a small-org audit screen (per_page 200
  // comfortably covers real headcount); if that ever gets tight, paginate
  // like every other admin list instead of raising the cap further.
  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users-email-access'],
    queryFn: () => adminUsersApi.list({ per_page: 200 })
  }));
  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));

  const admins = $derived((usersQuery.data?.results ?? []).filter((u) => u.role === 'admin'));
  const verticalSections = $derived.by(() => {
    const users = usersQuery.data?.results ?? [];
    return (verticalsQuery.data?.results ?? []).map((v) => ({
      vertical: v,
      users: users.filter((u) => u.role !== 'admin' && (u.vertical_id === v.id || u.verticals.includes(v.id)))
    }));
  });

  const toggleEmailAccessMutation = createMutation(() => ({
    mutationFn: (row: AdminUser) => adminUsersApi.update(row.id, { email_access: !row.email_access }),
    onSuccess: () => {
      // Same underlying row as the Users & Access tab's toggle — keep both
      // views' caches in sync.
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-email-access'] });
    }
  }));

  function seesSummary(row: AdminUser, verticalName: string): string {
    if (row.role === 'admin') return 'All emails incl. management-only';
    return row.email_access ? `${verticalName} team emails` : 'No emails';
  }

  function mailboxDotColor(row: AdminUser): string {
    if (row.email_account_status === 'connected') return '#97D700';
    if (row.email_account_status === 'error') return '#C0392B';
    return '#DBDBDB';
  }
  // email_account_status/address reflect only ONE representative account —
  // email_account_count (see handlers.userJSON) is what makes a second+
  // connected mailbox visible here instead of silently implying "at most
  // one"; the full per-mailbox breakdown lives in Admin > Emails > All
  // inboxes.
  function mailboxLabel(row: AdminUser): string {
    const extra = row.email_account_count > 1 ? ` (+${row.email_account_count - 1} more)` : '';
    if (!row.email_account_status) return 'No mailbox connected';
    if (row.email_account_status === 'connected') return `Connected — ${row.email_account_address}${extra}`;
    return `Error — ${row.email_account_address}${extra}`;
  }
  function lastSyncedLabel(row: AdminUser): string {
    return row.email_account_last_synced_at ? `Last synced ${formatIST(row.email_account_last_synced_at)}` : '';
  }
</script>

{#snippet accessRow(row: AdminUser, verticalName: string)}
  <tr>
    <td>
      <div class="text-[13.5px] font-semibold text-navy">{row.name || row.email}</div>
      <div class="text-[11.5px] text-dark-gray">{row.email}</div>
    </td>
    <td>
      <span
        class="tym-badge capitalize"
        style={row.role === 'admin' ? 'background:#E6E9EE; color:#10223A' : 'background:#F1F0EF; color:#666'}
        >{row.role}</span
      >
    </td>
    <td>
      <button
        type="button"
        title="Allow connecting a mailbox and seeing synced email touchpoints"
        class="inline-flex items-center gap-2 border-0 bg-transparent p-0"
        onclick={() => toggleEmailAccessMutation.mutate(row)}
      >
        <span
          class="relative inline-block h-5 w-[34px] rounded-pill transition-colors"
          style="background:{row.email_access ? '#97D700' : '#DBDBDB'}"
        >
          <span
            class="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_3px_rgba(16,34,58,.3)] transition-[left]"
            style="left:{row.email_access ? '16px' : '2px'}"
          ></span>
        </span>
        <span class="text-[11.5px] font-semibold" style="color:{row.email_access ? '#10223A' : '#999D9E'}"
          >Email access</span
        >
      </button>
    </td>
    <td class="muted">
      <div class="flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full" style="background:{mailboxDotColor(row)}"></span>
        {mailboxLabel(row)}
      </div>
      {#if lastSyncedLabel(row)}
        <div class="mt-0.5 text-[11px] text-dark-gray">{lastSyncedLabel(row)}</div>
      {/if}
    </td>
    <td class="muted">{seesSummary(row, verticalName)}</td>
  </tr>
{/snippet}

<div>
  <div class="mb-[18px]">
    <h2 class="tym-section-title">Email access</h2>
    <div class="tym-section-sub">Who can read what — grouped by vertical, plus the admins who see everything</div>
  </div>

  {#if usersQuery.isLoading || verticalsQuery.isLoading}
    <p class="tym-loading-state">Loading&hellip;</p>
  {:else if usersQuery.isError}
    <p class="tym-error-state">{getErrorMessage(usersQuery.error)}</p>
  {:else if verticalsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(verticalsQuery.error)}</p>
  {:else}
    <div class="mb-6">
      <div class="mb-2 flex items-center gap-2">
        <ShieldCheck class="h-4 w-4 shrink-0 text-toyama-teal" />
        <h3 class="font-heading text-[14px] font-bold text-navy">Admins — see everything incl. management-only</h3>
      </div>
      <div class="tym-table-wrap">
        <div class="tym-table-scroll">
          <table class="tym-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Email access</th>
                <th>Mailbox</th>
                <th>Sees</th>
              </tr>
            </thead>
            <tbody>
              {#each admins as row (row.id)}
                {@render accessRow(row, '')}
              {:else}
                <tr><td class="empty" colspan="5">No admin accounts.</td></tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    {#each verticalSections as section (section.vertical.id)}
      <div class="mb-6">
        <div class="mb-2 flex items-center gap-2">
          <Mail class="h-4 w-4 shrink-0 text-dark-gray" />
          <h3 class="font-heading text-[14px] font-bold text-navy">{section.vertical.name}</h3>
        </div>
        <div class="tym-table-wrap">
          <div class="tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Email access</th>
                  <th>Mailbox</th>
                  <th>Sees</th>
                </tr>
              </thead>
              <tbody>
                {#each section.users as row (row.id)}
                  {@render accessRow(row, section.vertical.name)}
                {:else}
                  <tr><td class="empty" colspan="5">No members in this vertical.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    {/each}
  {/if}
</div>
